/**
 * UTC timestamp rendering for the report pages (work item 17434).
 *
 * DateRangePicker bounds a custom range on the UTC day (dateInputToUtcIso maps
 * "yyyy-MM-dd" to 00:00:00.000Z / 23:59:59.999Z) and every server metric is
 * computed in UTC (MetricsAggregator works from UtcNow.Date). The report tables
 * used to render rows with toLocaleString(), so an operator west of Greenwich
 * saw a row stamped 2026-09-07 inside a range they picked as 2026-09-08, and
 * read it as a wrong number rather than a zone mismatch.
 *
 * The filter is the side that cannot move - it has to match the server - so the
 * display moves to UTC and says so on every value.
 *
 * A date-time with no zone designator is read as UTC (work item 21484), not as
 * the browser's local time which is what `new Date` does with it.
 */

const TIMESTAMP_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'UTC',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const DATE_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'UTC',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The suffix every UTC-rendered value carries, so the zone is never implied. */
export const UTC_SUFFIX = 'UTC';

const ZONELESS_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

// EF Unspecified-Kind DateTimes serialise with no Z, and `new Date` would read them as local time.
function parseUtc(iso: string): Date {
  return new Date(ZONELESS_DATE_TIME.test(iso) ? `${iso}Z` : iso);
}

/**
 * "2026-09-07 21:00 UTC". Returns an em dash for a null, undefined or
 * unparseable value, matching the placeholder the report tables already use.
 */
export function fmtUtcDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = parseUtc(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${TIMESTAMP_FORMAT.format(d).replace(', ', ' ')} ${UTC_SUFFIX}`;
}

/** "2026-09-07 UTC" - for values whose time of day carries no meaning. */
export function fmtUtcDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = parseUtc(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${DATE_FORMAT.format(d)} ${UTC_SUFFIX}`;
}
